import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { card, fmtDate, waLink, btnGhost } from "./ui";
import { lateness } from "./ui";
import PatientChat from "./prontuario/PatientChat";
import { Loader2, ArrowLeft, Rows3, Columns3, ExternalLink, Copy, UserPlus, MessageCircle, Video, Check, Sparkles } from "lucide-react";

// Jitsi preservado no código, oculto da interface — para reverter a alteração, mude para true.
const JITSI_ENABLED = false;

export default function SessionRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [meetLink, setMeetLink] = useState("");
  const [meetInput, setMeetInput] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [gConnected, setGConnected] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [patientPhone, setPatientPhone] = useState("");
  const [layout, setLayout] = useState(() => localStorage.getItem("room_layout") || "stacked");
  const [mode, setMode] = useState(() => localStorage.getItem("room_mode") || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: s } = await api.get(`/sessions/${id}`);
        if (!alive) return;
        setSession(s);
        setMeetLink(s.meet_link || "");
        setMeetInput(s.meet_link || "");
        if (!localStorage.getItem("room_mode")) {
          setMode(s.meet_link && !s.meet_link.includes("meet.jit.si") ? "meet" : "jitsi");
        }
        try {
          const { data: g } = await api.get("/google/status");
          if (alive) setGConnected(Boolean(g.enabled && g.connected));
        } catch { /* integração Google inativa */ }
        let pid = s.patient_id;
        if (!pid) {
          const { data: patients } = await api.get("/patients");
          const p = patients.find((x) => (x.name || "").toLowerCase() === (s.patient_name || "").toLowerCase());
          pid = p?.id || null;
          if (p?.phone) setPatientPhone(p.phone);
        } else {
          try {
            const { data: p } = await api.get(`/patients/${pid}`);
            if (alive && p?.phone) setPatientPhone(p.phone);
          } catch { /* sem telefone */ }
        }
        if (alive) setPatientId(pid);
      } catch {
        if (alive) toast.error("Não foi possível abrir a sala desta sessão.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const setL = (v) => { setLayout(v); localStorage.setItem("room_layout", v); };
  const setM = (v) => { setMode(v); localStorage.setItem("room_mode", v); };

  const saveMeetLink = async () => {
    const link = meetInput.trim();
    if (link && !/^https?:\/\//i.test(link)) {
      return toast.error("Cole um link válido, começando com https://");
    }
    setSavingLink(true);
    try {
      const { data } = await api.patch(`/sessions/${id}/meet-link`, { meet_link: link });
      setMeetLink(data.meet_link || "");
      setMeetInput(data.meet_link || "");
      toast.success(link ? "Link da videochamada salvo nesta sessão." : "Link removido desta sessão.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar o link.");
    } finally {
      setSavingLink(false);
    }
  };

  const genAuto = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/sessions/${id}/meet-auto`);
      setMeetLink(data.meet_link || "");
      setMeetInput(data.meet_link || "");
      toast.success("Link do Meet gerado e salvo nesta sessão.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui gerar o link automático.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" data-testid="room-loading" />;
  if (!session) return <p className="font-body text-sm text-[#7A8A7A]">Sessão não encontrada.</p>;

  const jitsiLink = (meetLink || "").includes("meet.jit.si") ? meetLink : `https://meet.jit.si/TacilainePsi-${id.replace(/-/g, "")}`;
  const jitsiSrc = `${jitsiLink}#config.prejoinPageEnabled=false&config.disableDeepLinking=true&userInfo.displayName=%22Tacilaine%20Almeida%22`;
  // Copiar/enviar usa SEMPRE o link salvo na sessão (inclusive links antigos preservados).
  const activeLink = JITSI_ENABLED && mode === "jitsi" ? jitsiLink : meetLink;
  const side = layout === "side";
  // código curto e estável da sessão — o mesmo que aparece no título do evento no Google
  const sessionRef = (session.id || "").replace(/-/g, "").slice(0, 6).toUpperCase();

  const copyLink = () => {
    if (!activeLink) return toast.error("Crie e salve o link do Google Meet antes de copiar o convite.");
    navigator.clipboard?.writeText(activeLink).then(
      () => toast.success("Link copiado! Envie ao paciente pelo WhatsApp ou e-mail."),
      () => toast.error("Não consegui copiar. Selecione o link e copie manualmente."),
    );
  };

  // Convite de videochamada: usa SEMPRE o link salvo na sessão; o envio é manual no WhatsApp.
  const buildInviteMsg = () => {
    const firstName = (session?.patient_name || "").trim().split(" ")[0] || "";
    const linkLabel = activeLink.includes("meet.google.com") ? "Link da videochamada (Google Meet)" : "Link da videochamada";
    return [
      `Olá, ${firstName}! Aqui é a Tacilaine.`,
      "",
      "*Nossa sessão de psicoterapia online*",
      `Data: ${fmtDate(session.date)}`,
      `Horário: ${session.time}`,
      "",
      `${linkLabel}:`,
      `${activeLink}`,
      "",
      "Para entrar, é só clicar no link alguns minutos antes do horário. Se tiver qualquer dificuldade, me avise por aqui.",
    ].join("\n");
  };

  const sendWhatsApp = () => {
    if (!activeLink) return toast.error("Crie e salve o link do Google Meet antes de enviar o convite.");
    const url = waLink(patientPhone, buildInviteMsg());
    if (!url) return toast.error("Este paciente não tem WhatsApp cadastrado. Adicione o telefone em Pacientes.");
    window.open(url, "_blank");
  };

  const toggleBtn = (v, Icon, txt, tid) => (
    <button onClick={() => setL(v)} data-testid={tid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body transition-colors ${layout === v ? "bg-[#5B6E5B] text-[#FDFBF7]" : "text-[#4A5D4E] hover:bg-white"}`}>
      <Icon className="w-3.5 h-3.5" /> {txt}
    </button>
  );
  const modeBtn = (v, txt, tid) => (
    <button onClick={() => setM(v)} data-testid={tid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body transition-colors ${mode === v ? "bg-[#5B6E5B] text-[#FDFBF7]" : "text-[#4A5D4E] hover:bg-white"}`}>
      {txt}
    </button>
  );

  const confirmAbsence = async () => {
    try {
      const { data } = await api.patch(`/sessions/${id}/status`, { status: "falta" });
      setSession(data);
      toast.success("Ausência confirmada. A cobrança permanece conforme a política acordada com o paciente.");
    } catch { toast.error("Não consegui confirmar a ausência."); }
  };

  return (
    <div data-testid="session-room" className="max-w-[1400px]">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/dashboard/agenda")} data-testid="room-back"
            className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <p className="font-body text-[10px] sm:text-xs tracking-[0.35em] uppercase text-[#C87A5E]" data-testid="room-eyebrow">Sala de atendimento · Sessão {sessionRef}</p>
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl text-[#2C332A] leading-tight mt-1 truncate" data-testid="room-title">
              {session.patient_name} · {fmtDate(session.date)} às {session.time}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {JITSI_ENABLED && (
          <div className="inline-flex items-center rounded-full bg-[#F5F0E6] p-1" data-testid="room-mode-toggle">
            {modeBtn("jitsi", "Vídeo embutido", "mode-jitsi")}
            {modeBtn("meet", "Google Meet", "mode-meet")}
          </div>
          )}
          <div className="inline-flex items-center rounded-full bg-[#F5F0E6] p-1" data-testid="room-layout-toggle">
            {toggleBtn("stacked", Rows3, "Empilhado", "layout-stacked")}
            {toggleBtn("side", Columns3, "Lado a lado", "layout-side")}
          </div>
          <button onClick={sendWhatsApp} data-testid="send-whatsapp"
            className={`inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm text-white hover:bg-[#1EBE5A] transition-colors ${!meetLink ? "opacity-50" : ""}`}>
            <MessageCircle className="w-4 h-4" /> <span className="hidden sm:inline">Enviar no </span>WhatsApp
          </button>
          <button onClick={copyLink} data-testid="copy-patient-link"
            className={`inline-flex items-center gap-2 rounded-full border border-[#5B6E5B]/25 px-4 py-2 text-sm text-[#5B6E5B] hover:bg-white transition-colors ${!meetLink ? "opacity-50" : ""}`}>
            <Copy className="w-4 h-4" /> <span className="hidden sm:inline">Copiar </span>link
          </button>
        </div>
      </div>

      {/* regra de atraso: apenas SINALIZA — a ausência é sempre confirmada manualmente */}
      {session.status === "agendada" && lateness(session) === "excedida" && (
        <div className={`${card} p-4 mb-4 border-l-4 border-l-[#C87A5E]`} data-testid="lateness-alert">
          <p className="font-body text-sm text-[#B2644D]">
            <strong>Tolerância excedida.</strong> A sessão começaria às {session.time}. Passados os 20 minutos de tolerância,
            ela pode ser considerada ausência — com a cobrança mantida conforme a política acordada com o paciente.
          </p>
          <button onClick={confirmAbsence} className={`${btnGhost} mt-2.5`} data-testid="confirm-absence">
            <Check className="w-4 h-4" /> Confirmar ausência (falta)
          </button>
        </div>
      )}
      {session.status === "agendada" && lateness(session) === "em_tolerancia" && (
        <p className="font-body text-xs text-[#7A8A7A] mb-4" data-testid="lateness-info">
          Sessão em andamento — atraso de até 20 minutos está dentro da tolerância; o término permanece no horário previsto.
        </p>
      )}

      <div className={side ? "grid lg:grid-cols-[1.7fr_1fr] gap-4 sm:gap-5 items-start" : "flex flex-col gap-4 sm:gap-5"}>
        {JITSI_ENABLED && mode === "jitsi" ? (
          <div className={`rounded-2xl overflow-hidden border border-[#5B6E5B]/15 bg-black ${side ? "h-[52vh] lg:h-[calc(100vh-11rem)] min-h-[320px]" : "h-[48vh] sm:h-[54vh] min-h-[300px]"}`} data-testid="room-video">
            <iframe
              title="Videochamada Jitsi"
              src={jitsiSrc}
              allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
              className="w-full h-full border-0"
            />
          </div>
        ) : (
          <div className={`${card} p-5 sm:p-6 ${side ? "min-h-[320px]" : ""}`} data-testid="room-meet-panel">
            <div className="flex items-center gap-3 mb-4">
              <div className="grid place-items-center w-10 h-10 rounded-full bg-[#F5F0E6]"><Video className="w-5 h-5 text-[#5B6E5B]" /></div>
              <div>
                <p className="font-display text-lg text-[#2C332A]">Google Meet</p>
                <p className="font-body text-xs text-[#7A8A7A]">O Meet abre em outra aba — deixe esta tela ao lado para anotar com a IA.</p>
              </div>
            </div>

            {gConnected && !meetLink && (
              <div className="mb-4">
                <button onClick={genAuto} disabled={generating} data-testid="auto-meet-link"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C87A5E] px-5 py-3 text-sm text-[#FDFBF7] hover:bg-[#B56A50] transition-colors disabled:opacity-60">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Gerar link do Meet automaticamente
                </button>
                <p className="font-body text-xs text-[#7A8A7A] mt-2 text-center">com o Google conectado, o link é criado sozinho — ou cole manualmente abaixo</p>
              </div>
            )}
            {!gConnected && (
            <ol className="font-body text-sm text-[#4A5D4E] space-y-1.5 mb-4 list-decimal list-inside">
              <li>Abra o <a href="https://meet.google.com/home" target="_blank" rel="noreferrer" className="underline text-[#5B6E5B]" data-testid="meet-new-link">Google Meet</a>, inicie a reunião e copie o link.</li>
              <li>Cole o link abaixo e salve — ele fica guardado nesta sessão.</li>
              <li>Envie o mesmo link ao paciente pelo WhatsApp.</li>
            </ol>
            )}
            <div className="flex flex-col sm:flex-row gap-2" data-testid="meet-form">
              <input
                value={meetInput}
                onChange={(e) => setMeetInput(e.target.value)}
                placeholder="Cole aqui o link da sala do Meet"
                data-testid="meet-link-input"
                className="flex-1 rounded-xl border border-[#5B6E5B]/25 bg-white px-4 py-2.5 font-body text-sm text-[#2C332A] placeholder:text-[#7A8A7A]/60 focus:outline-none focus:border-[#5B6E5B]"
              />
              <button onClick={saveMeetLink} disabled={savingLink} data-testid="save-meet-link"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5B6E5B] px-5 py-2.5 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors disabled:opacity-60">
                {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Salvar link
              </button>
            </div>
            {meetInput.trim() !== meetLink ? (
              <p className="font-body text-xs text-[#C87A5E] mt-2" data-testid="meet-unsaved">Link alterado — clique em Salvar link.</p>
            ) : (
              <p className="font-body text-xs text-[#7A8A7A] mt-2">Aceita qualquer link de videochamada (Meet, Zoom etc.).</p>
            )}
            {meetLink && (
              <div className="mt-4" data-testid="meet-saved">
                <p className="font-body text-xs text-[#7A8A7A] mb-2" data-testid="meet-session-ref">
                  Sessão <strong className="text-[#4A5D4E]">{sessionRef}</strong> — o evento no Google Agenda aparece como "Atendimento online · {(session.patient_name || "").trim().split(" ")[0]} · Sessão {sessionRef}".
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href={activeLink} target="_blank" rel="noreferrer" data-testid="open-meet"
                    className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-5 py-2.5 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors">
                    <ExternalLink className="w-4 h-4" /> Abrir Google Meet
                  </a>
                  <button onClick={copyLink} data-testid="copy-meet-link"
                    className="inline-flex items-center gap-2 rounded-full border border-[#5B6E5B]/25 px-4 py-2.5 text-sm text-[#5B6E5B] hover:bg-white transition-colors">
                    <Copy className="w-4 h-4" /> Copiar link
                  </button>
                  <button onClick={sendWhatsApp} data-testid="whatsapp-meet-link"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm text-white hover:bg-[#1EBE5A] transition-colors">
                    <MessageCircle className="w-4 h-4" /> Enviar no WhatsApp
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className={`${card} p-4 sm:p-5`}>
          <p className="font-body text-xs tracking-[0.3em] uppercase text-[#7A8A7A] mb-3">Anotações da sessão (IA)</p>
          {patientId ? (
            <PatientChat
              patientId={patientId}
              patientName={session.patient_name}
              heightClass={side ? "h-[46vh] lg:h-[calc(100vh-19rem)] min-h-[300px]" : "h-[64vh] min-h-[520px]"}
              widthClass="max-w-none"
            />
          ) : (
            <div className="flex flex-col items-center text-center gap-4 py-10" data-testid="room-no-patient">
              <div className="grid place-items-center w-14 h-14 rounded-full bg-[#F5F0E6]"><UserPlus className="w-6 h-6 text-[#5B6E5B]" /></div>
              <p className="font-body text-sm text-[#7A8A7A] max-w-xs">Cadastre <strong>{session.patient_name}</strong> em Pacientes para anotar a evolução com a IA durante a sessão.</p>
              <button onClick={() => navigate("/dashboard/pacientes")} data-testid="room-go-patients"
                className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-5 py-2.5 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors">
                <UserPlus className="w-4 h-4" /> Ir para Pacientes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
